import { useCallback, useEffect, useRef, useState } from "react";

interface UseDropdownCompanionResult {
	wrapperRef: React.RefObject<HTMLDivElement | null>;
	isOpen: boolean;
	contentElement: HTMLElement | null;
}

const TRIGGER_SELECTOR = '[data-testid="vendor-filter"]';
const CONTENT_SELECTOR = '[data-side][data-state="open"]';

/**
 * Resolves the dropdown content owned by this trigger via the `aria-controls`
 * link Radix sets on it. Without this, any other open dropdown on the page
 * (e.g. the radius selector) competes for `contentElement` and the companion
 * map gets positioned against the wrong popup.
 */
export const resolveOwnContent = (trigger: HTMLElement): HTMLElement | null => {
	const contentId = trigger.getAttribute("aria-controls");
	if (!contentId) {
		return null;
	}
	const content = document.getElementById(contentId);
	return content?.dataset.state === "open" ? content : null;
};

/**
 * Observes a Radix Select trigger inside `wrapperRef` for open/close state,
 * and tracks the portal dropdown's bounding rect so a companion element
 * (e.g. a map panel) can be positioned next to it.
 */
export function useDropdownCompanion(): UseDropdownCompanionResult {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [contentElement, setContentElement] = useState<HTMLElement | null>(
		null,
	);

	useEffect(() => {
		const trigger =
			wrapperRef.current?.querySelector<HTMLElement>(TRIGGER_SELECTOR);
		if (!trigger) return;

		const observer = new MutationObserver(() => {
			setIsOpen(trigger.getAttribute("data-state") === "open");
		});

		observer.observe(trigger, {
			attributes: true,
			attributeFilter: ["data-state"],
		});

		return () => observer.disconnect();
	}, []);

	/**
	 * Updates the content element reference by finding the dropdown content
	 * associated with the trigger in this wrapper.
	 */
	const updateRect = useCallback(() => {
		const trigger =
			wrapperRef.current?.querySelector<HTMLElement>(TRIGGER_SELECTOR);
		if (!trigger) {
			setContentElement(null);
			return;
		}

		const ownContent = resolveOwnContent(trigger);
		if (ownContent) {
			setContentElement(ownContent);
			return;
		}

		const allOpenContents =
			document.querySelectorAll<HTMLElement>(CONTENT_SELECTOR);
		let matchingContent: HTMLElement | null = null;

		if (allOpenContents.length === 1) {
			matchingContent = allOpenContents[0];
		} else if (allOpenContents.length > 1) {
			const triggerRect = trigger.getBoundingClientRect();

			let closestContent: HTMLElement | null = null;
			let minDistance = Number.POSITIVE_INFINITY;

			allOpenContents.forEach((content) => {
				const contentRect = content.getBoundingClientRect();
				const distance = Math.abs(contentRect.top - triggerRect.top);
				if (distance < minDistance) {
					minDistance = distance;
					closestContent = content;
				}
			});

			matchingContent = closestContent;
		}

		if (!matchingContent) {
			setContentElement(null);
			return;
		}
		setContentElement(matchingContent);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setContentElement(null);
			return;
		}

		const frame = requestAnimationFrame(updateRect);
		const settled = setTimeout(updateRect, 200);

		return () => {
			cancelAnimationFrame(frame);
			clearTimeout(settled);
		};
	}, [isOpen, updateRect]);

	return { wrapperRef, isOpen, contentElement };
}
